// components/PageContentForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPageContentById, createPageContent, updatePageContent } from '../services/pageContentService';
import { TextField, Button, Box, Container, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import DashboardLayout from '../components/DashboardLayout';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MediaUploadField from './MediaUploadField';

const PageContentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    sectionName: '',
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    heading: '',
    content: '',
    imageURL: '',
  });

  useEffect(() => {
    if (id) {
      fetchPageContent();
    }
  }, [id]);

  const fetchPageContent = async () => {
    try {
      const data = await getPageContentById(id);
      setInitialValues(data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const validationSchema = Yup.object().shape({
    sectionName: Yup.string().required('Section Name is required'),
    metaTitle: Yup.string().max(200, 'Max 200 characters'),
    metaDescription: Yup.string().max(300, 'Max 300 characters'),
    keywords: Yup.string().max(300, 'Max 300 characters'),
    heading: Yup.string().max(100, 'Max 100 characters'),
    content: Yup.string(),
    imageURL: Yup.string().nullable(),
  });

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updatePageContent(id, values);
      } else {
        await createPageContent(values);
      }
      navigate('/page-contents');
    } catch (error) {
      console.error('Error saving page content:', error);
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit Page Content' : 'Create Page Content'}
          </Typography>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                <TextField
                  label="Section Name"
                  name="sectionName"
                  value={values.sectionName}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  error={touched.sectionName && Boolean(errors.sectionName)}
                  helperText={touched.sectionName && errors.sectionName}
                />
                <TextField
                  label="Meta Title"
                  name="metaTitle"
                  value={values.metaTitle}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  error={touched.metaTitle && Boolean(errors.metaTitle)}
                  helperText={touched.metaTitle && errors.metaTitle}
                />
                <TextField
                  label="Meta Description"
                  name="metaDescription"
                  value={values.metaDescription}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  multiline
                  error={touched.metaDescription && Boolean(errors.metaDescription)}
                  helperText={touched.metaDescription && errors.metaDescription}
                />
                <TextField
                  label="Keywords"
                  name="keywords"
                  value={values.keywords}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  error={touched.keywords && Boolean(errors.keywords)}
                  helperText={touched.keywords && errors.keywords}
                />
                <TextField
                  label="Heading"
                  name="heading"
                  value={values.heading}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  error={touched.heading && Boolean(errors.heading)}
                  helperText={touched.heading && errors.heading}
                />
                <MediaUploadField
                  label="Section Image"
                  value={values.imageURL}
                  onChange={(nextValue) => setFieldValue('imageURL', nextValue)}
                  kind="image"
                  scope="pagecontents"
                  slug={values.sectionName}
                  error={errors.imageURL}
                  touched={touched.imageURL}
                  helperText="Optional. Upload a managed image or keep a manual URL."
                />
                
                {/* React Quill for Rich Text Content */}
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>
                    Content
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.content}
                    onChange={(content) => setFieldValue('content', content)}
                    modules={{
                      toolbar: [
                        [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['bold', 'italic', 'underline'],
                        ['link'],
                        [{ 'align': [] }],
                        ['clean']
                      ],
                    }}
                    error={touched.content && Boolean(errors.content)}
                    helperText={touched.content && errors.content}
                    className="react-quill-editor"
                  />
                </Box>

                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    {id ? 'Update Content' : 'Create Content'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default PageContentForm;
